import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CircleCheckIcon,
  CircleMinusIcon,
  CircleXIcon,
  SearchIcon,
} from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { memo, useMemo, useState } from 'react';
import styled from 'styled-components';

import { LinkifyExternal } from 'dashboard/components/LinkifyExternal';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { formatAddress } from 'dashboard/lib/utils/formatters';
import { nameCase } from 'dashboard/lib/utils/stringFormat';
import {
  Address,
  AssociatedPerson,
  AssociatedPersonPosition,
  RegistryDocument,
  Status,
  UsSecretaryOfStateRecord,
} from 'dashboard/models/registry-record';
import Attribute from 'design-system/components/Attribute';
import AttributeGrid from 'design-system/components/Attribute/AttributeGrid';
import Button, { IconButton } from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Header from 'design-system/components/Card/Header';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { US_SECRETARY_OF_STATES, UsSecretaryOfState } from 'lib/constants/usSecretaryOfStates';

const Grid = styled.div`
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-row-gap: ${Spacing[4]};
  margin: ${Spacing[4]} 0;
`;

const FilingStatusSticker = ({ status }: { status: Status }) => {
  switch (status.standardized || 'unknown') {
    case 'active':
      return (
        <Sticker icon={<CircleCheckIcon />} variant='green'>
          {status.raw}
        </Sticker>
      );
    case 'inactive':
      return (
        <Sticker icon={<CircleXIcon />} variant='danger'>
          {status.raw}
        </Sticker>
      );
    default:
      return (
        <Sticker icon={<CircleMinusIcon />} variant='grey'>
          {status.raw ?? 'Unknown'}
        </Sticker>
      );
  }
};

const StandardizedStatusSticker = ({ status }: { status: Status }) => {
  switch (status.standardized || 'unknown') {
    case 'active':
      return <Sticker variant='green'>Active</Sticker>;
    case 'inactive':
      return <Sticker variant='danger'>Inactive</Sticker>;
    default:
      return <Sticker variant='grey'>Unknown</Sticker>;
  }
};

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const StyledCard = styled(Card)`
  border-radius: 5px;
  border: 1px solid ${Color.CoreSlate200};
`;

const FileDetails = ({ registryRecord }: { registryRecord: UsSecretaryOfStateRecord }) => {
  const website =
    US_SECRETARY_OF_STATES[registryRecord['issuing-authority'] as UsSecretaryOfState].website;
  const name =
    US_SECRETARY_OF_STATES[registryRecord['issuing-authority'] as UsSecretaryOfState].name;
  return (
    <Paragraph color='primary'>
      {registryRecord['file-number'] && `File number: ${registryRecord['file-number']}`}
      {registryRecord['file-number'] && website && ' · '}
      {website && (
        <LinkifyExternal>
          <a href={website}>{name}</a>
        </LinkifyExternal>
      )}
    </Paragraph>
  );
};

const DEFAULT_DOCUMENTS_LENGTH = 5;

interface RegistryRecordProps {
  registryRecord: UsSecretaryOfStateRecord;
  isRedacted?: boolean;
}
const RegistryRecord = ({ registryRecord, isRedacted }: RegistryRecordProps) => {
  const [showAllDocuments, setShowAllDocuments] = useState(false);

  const registeredAgents = (registryRecord['associated-persons'] ?? []).filter(
    (person: AssociatedPerson) =>
      (person['positions'] ?? []).some(
        (position: AssociatedPersonPosition) =>
          position.value && /registered agent/gi.test(position.value),
      ),
  );

  const people = (registryRecord['associated-persons'] ?? []).filter((person: AssociatedPerson) =>
    (person['positions'] ?? []).some(
      (position: AssociatedPersonPosition) =>
        position.value && !/registered agent/gi.test(position.value),
    ),
  );

  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const documentsFuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      findAllMatches: true,
      keys: ['name', 'date'],
    };

    return new Fuse(registryRecord.documents ?? [], fuseOptions);
  }, [registryRecord]);

  const searchedDocuments = useMemo(
    () =>
      search === '' || !showSearch
        ? (registryRecord.documents ?? [])
        : documentsFuse.search(search).map(({ item }) => item),
    [documentsFuse, registryRecord.documents, search, showSearch],
  );

  return (
    <StyledCard>
      <Card.Body>
        <Card.Title>
          <div>
            <HorizontalContainer>
              {registryRecord['registration-state']}
              <StandardizedStatusSticker status={registryRecord.status} />
            </HorizontalContainer>
            <FileDetails registryRecord={registryRecord} />
          </div>
        </Card.Title>
        <section>
          <Card.SectionHeader>Details</Card.SectionHeader>
          <AttributeGrid horizontal='full-width'>
            <Attribute
              label='Name'
              value={registryRecord['registered-name']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Registration Issue Date'
              value={registryRecord['issue-date']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Jurisdiction'
              value={registryRecord['registration-type']}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Filing Status'
              value={<FilingStatusSticker status={registryRecord.status} />}
              placeholder='Unknown'
              redacted={isRedacted}
            />
          </AttributeGrid>
        </section>
        {registryRecord.addresses && (
          <section>
            <Card.SectionHeader>Company Addresses</Card.SectionHeader>
            <AttributeGrid horizontal='full-width'>
              {registryRecord['addresses'].map((address: Address, index: number) => {
                return (
                  <Attribute
                    key={`address__${index}`}
                    label={address.type}
                    value={formatAddress({
                      addressStreet1: address['street-1'] ?? '',
                      addressStreet2: address['street-2'] ?? '',
                      addressCity: address.city ?? '',
                      addressSubdivision: address.subdivision,
                      addressPostalCode: address['postal-code'],
                      color: 'primary',
                    })}
                    redacted={isRedacted}
                  />
                );
              })}
            </AttributeGrid>
          </section>
        )}
        {people.length > 0 && (
          <section>
            <Card.SectionHeader>People</Card.SectionHeader>
            <AttributeGrid horizontal='full-width'>
              {people.map((person: AssociatedPerson, index: number) => (
                <Attribute
                  key={`person__${index}`}
                  label={
                    nameCase(
                      person?.positions?.find(
                        (position: AssociatedPersonPosition) =>
                          position.value !== 'registered agent',
                      )?.value,
                    ) ?? 'Person'
                  }
                  value={person.names[0]?.value}
                  placeholder='Unknown'
                  redacted={isRedacted}
                />
              ))}
            </AttributeGrid>
          </section>
        )}
        {registeredAgents.length > 0 && (
          <section>
            <Card.SectionHeader>Registered Agents</Card.SectionHeader>
            <AttributeGrid horizontal='full-width'>
              {registeredAgents.map((agent: AssociatedPerson, index: number) => (
                <Attribute
                  key={`registered-agent__${index}`}
                  label='Name'
                  value={agent.names[0]?.value}
                  placeholder='Unknown'
                  redacted={isRedacted}
                />
              ))}
            </AttributeGrid>
          </section>
        )}
        {registryRecord.documents && registryRecord.documents.length > 0 && (
          <section>
            <Header
              variant='section'
              actions={
                <IconButton
                  aria-labelledby='documents__show_search_button_label'
                  id='documents__show_search_button'
                  onClick={() => setShowSearch(!showSearch)}
                >
                  <span hidden id='documents__show_search_button_label'>
                    Toggle documents search
                  </span>
                  <SearchIcon />
                </IconButton>
              }
            >
              {`Documents (${
                searchedDocuments.length !== registryRecord.documents.length
                  ? searchedDocuments.length + ' of '
                  : ''
              }${registryRecord.documents.length})`}
            </Header>
            {showSearch && (
              <SearchInput
                id='documents__search'
                title={null}
                handleInput={setSearch}
                value={search}
                placeholder='Search documents'
                size='md'
              />
            )}
            <Grid>
              {searchedDocuments
                .slice(0, showAllDocuments ? searchedDocuments.length : DEFAULT_DOCUMENTS_LENGTH)
                .map((document: RegistryDocument, index: number) => (
                  <div key={`document__${index}`}>
                    <LinkifyExternal key={`document__${index}`}>
                      <a target='_blank' rel='noreferrer' href={document['url']}>
                        {`${document['name']} (${document['date']})`}
                      </a>
                    </LinkifyExternal>
                  </div>
                ))}
            </Grid>
            {searchedDocuments.length > DEFAULT_DOCUMENTS_LENGTH && (
              <Button
                id='button__toggle-documents'
                width='auto'
                variant='basic'
                onClick={() => setShowAllDocuments(!showAllDocuments)}
              >
                {showAllDocuments
                  ? 'Show less'
                  : `Show ${searchedDocuments.length - DEFAULT_DOCUMENTS_LENGTH} more`}
              </Button>
            )}
          </section>
        )}
      </Card.Body>
    </StyledCard>
  );
};

export default memo(RegistryRecord);
